import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

/** A "← Back" control that returns the user to wherever they actually
 * came from — real browser-history back — instead of one hardcoded
 * parent route.
 *
 * Bug this fixes (2026-09-18): every "← Back" link across the app
 * pointed at a single fixed route each (always `/contacts`, always
 * `/campaigns`, …). A beneficiary opened from a CAMPAIGN's own list —
 * or a session opened from the calendar, or a ticket opened from a
 * notification — landed back on the generic top-level list instead of
 * wherever the operator actually came from: "When I say return it
 * returns... somewhere else, and it's on many places."
 *
 * `createBrowserRouter`'s history stamps `window.history.state.idx`
 * with this browser tab's position in ITS OWN in-app history stack;
 * `idx === 0` means this is the first entry (a fresh tab, a direct
 * link, a reload) — there is nothing in-app to go back to, so we fall
 * back to `fallback` rather than navigating the user out of the SPA
 * entirely (what bare `navigate(-1)` would do in that case).
 */
export function BackLink({
  fallback,
  className,
  children,
}: {
  /** Route to land on when there's no in-app history to go back to. */
  fallback: string;
  className?: string;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className={className}
      onClick={() => {
        const idx = (window.history.state as { idx?: number } | null)?.idx;
        if (typeof idx === "number" && idx > 0) {
          navigate(-1);
        } else {
          navigate(fallback);
        }
      }}
    >
      {children}
    </button>
  );
}
